import { NavLink } from 'react-router'
import { LayoutDashboard, Users, Phone, CheckSquare, MoreHorizontal } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useState, useEffect, useRef } from 'react'
import {
  ArrowRightLeft,
  Building2,
  FileText,
  BookOpen,
  Inbox,
  Mail,
  MessageSquare,
  Wrench,
  BarChart3,
  Settings,
  Calendar,
  CalendarClock,
  DoorOpen,
  Share2,
  Mic,
  ListChecks,
  LayoutTemplate,
}  from 'lucide-react'

const primaryTabs = [
  { to: '/', icon: LayoutDashboard, label: 'Home' },
  { to: '/contacts', icon: Users, label: 'Contacts' },
  { to: '/calls', icon: Phone, label: 'Calls' },
  // The "Tasks" tab routes to the Tasks page (was mislabelled onto /follow-up,
  // which left the real /tasks page unreachable from mobile). Follow-Up moved
  // into the More sheet below so both stay reachable.
  { to: '/tasks', icon: CheckSquare, label: 'Tasks' },
]

const moreItems = [
  // ⚠️ THIS ENTRY IS NOT OPTIONAL, and it is not a duplicate of the Sidebar's.
  // These two arrays are maintained independently — there is no shared source and no test
  // enumerating either — and the Sidebar is `hidden md:block`, so a Sidebar-only page is simply
  // UNREACHABLE on a phone (/review, /fsbo and /commissions are in that state today). Plaud is
  // a POCKET recorder: the agent walks out of a showing and files the recording on their phone.
  // A desktop-only triage queue for a mobile-first device would be the wrong half shipped.
  { to: '/recordings', icon: Mic, label: 'Recordings' },
  { to: '/follow-up', icon: Mail, label: 'Follow-Up' },
  // RIZ047: the template library. Same entry in the Sidebar — keep the two together.
  { to: '/templates', icon: LayoutTemplate, label: 'Templates' },
  { to: '/referrals', icon: Share2, label: 'Referrals' },
  // Same rule as the warning above: Sidebar-only would make the directory unreachable
  // on a phone, and looking up a plumber standing in somebody's kitchen is the case it
  // exists for.
  { to: '/vendors', icon: Wrench, label: 'Vendors' },
  { to: '/calendar', icon: Calendar, label: 'Calendar' },
  { to: '/transactions', icon: ArrowRightLeft, label: 'Transactions' },
  // RIZ051: the Action Plans page. Sidebar-only would make it unreachable on a phone
  // (the rule above); same entry there — keep the two together.
  { to: '/transactions/plans', icon: ListChecks, label: 'Action Plans' },
  { to: '/deadlines', icon: CalendarClock, label: 'Deadlines' },
  { to: '/showings', icon: DoorOpen, label: 'Showings' },
  { to: '/properties', icon: Building2, label: 'Listings' },
  { to: '/ccr-library', icon: BookOpen, label: 'CC and R Library' },
  { to: '/documents', icon: FileText, label: 'Documents' },
  // Forms (/forms) is deliberately NOT listed — hidden 2026-09-09 on John's call while tracker
  // row 71 (the forms' Certificate of Authenticity) sits with HELLA's attorney. Same note in
  // Sidebar; restore both together.
  { to: '/inbox', icon: Inbox, label: 'Inbox' },
  { to: '/texting', icon: MessageSquare, label: 'Texting' },
  { to: '/analytics', icon: BarChart3, label: 'Analytics' },
  { to: '/settings', icon: Settings, label: 'Settings' },
]

export function MobileBottomNav() {
  const [moreOpen, setMoreOpen] = useState(false)
  const sheetRef = useRef<HTMLDivElement>(null)

  // Close on Escape key
  useEffect(() => {
    if (!moreOpen) return
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setMoreOpen(false)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [moreOpen])

  // Move focus into sheet when it opens
  useEffect(() => {
    if (moreOpen && sheetRef.current) {
      const first = sheetRef.current.querySelector<HTMLElement>(
        'a[href], button, [tabindex]:not([tabindex="-1"])'
      )
      first?.focus()
    }
  }, [moreOpen])

  return (
    <>
      {/* Backdrop */}
      {moreOpen && (
        <div
          className="fixed inset-0 z-40 md:hidden"
          onClick={() => setMoreOpen(false)}
          aria-hidden="true"
        >
          <div className="absolute inset-0 bg-black/40" />
        </div>
      )}

      {/* More navigation sheet */}
      {moreOpen && (
        <div
          ref={sheetRef}
          role="dialog"
          aria-modal="true"
          aria-label="More navigation options"
          className="fixed bottom-16 left-0 right-0 z-50 rounded-t-xl border-t border-border bg-background p-2 md:hidden"
        >
          <div className="grid grid-cols-3 gap-1">
            {moreItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={() => setMoreOpen(false)}
                className={({ isActive }) =>
                  cn(
                    'flex flex-col items-center gap-1 rounded-lg px-2 py-3 text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background',
                    isActive
                      ? 'bg-primary/10 text-primary'
                      : 'text-muted-foreground hover:bg-muted'
                  )
                }
              >
                <item.icon className="h-5 w-5" aria-hidden="true" />
                {item.label}
              </NavLink>
            ))}
          </div>
        </div>
      )}

      {/* Bottom nav bar */}
      <nav
        aria-label="Mobile navigation"
        className="fixed bottom-0 left-0 right-0 z-50 border-t border-border bg-background md:hidden"
      >
        <div className="flex items-stretch">
          {primaryTabs.map((tab) => (
            <NavLink
              key={tab.to}
              to={tab.to}
              end={tab.to === '/'}
              className={({ isActive }) =>
                cn(
                  'flex flex-1 flex-col items-center gap-0.5 py-2 text-[10px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
                  isActive ? 'text-primary' : 'text-muted-foreground'
                )
              }
            >
              <tab.icon className="h-5 w-5" aria-hidden="true" />
              {tab.label}
            </NavLink>
          ))}
          <button
            onClick={() => setMoreOpen(!moreOpen)}
            aria-expanded={moreOpen}
            aria-haspopup="dialog"
            aria-label={moreOpen ? 'Close more navigation' : 'Open more navigation'}
            className={cn(
              'flex flex-1 flex-col items-center gap-0.5 py-2 text-[10px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
              moreOpen ? 'text-primary' : 'text-muted-foreground'
            )}
          >
            <MoreHorizontal className="h-5 w-5" aria-hidden="true" />
            More
          </button>
        </div>
        {/* Safe area for iOS */}
        <div className="h-[env(safe-area-inset-bottom)]" aria-hidden="true" />
      </nav>
    </>
  )
}
